import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import {
  ShieldCheck,
  Lock,
  Phone,
  MapPin,
  Store as StoreIcon,
  Bike,
  Camera,
  PenLine,
  ArrowLeft,
  Loader2,
} from "lucide-react";

const SESSION_KEY = (code) => `db_recipient_unlock_${code}`;

function StatusPill({ status, label }) {
  const tone = {
    DELIVERED: "bg-green-500/10 text-green-700 dark:text-green-300 border-green-500/30",
    OUT_FOR_LAST_MILE: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30",
    SHIPPED_OUT: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30",
    RECEIVED_AT_WAREHOUSE: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30",
    PICKED_UP_BY_RIDER: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
    WAITING_FOR_PICKUP: "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  }[status] || "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-current/20 text-[11px] font-mono-tech uppercase tracking-wider ${tone}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
      {label}
    </span>
  );
}

function UnlockGate({ code, onUnlocked }) {
  const [last4, setLast4] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const digits = last4.replace(/\D/g, "");
    if (digits.length < 4) {
      setError("Enter the last 4 digits.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const r = await api.post(`/public/track/${encodeURIComponent(code)}/recipient`, {
        phone_last4: digits.slice(-4),
      });
      try {
        sessionStorage.setItem(SESSION_KEY(code), JSON.stringify(r.data));
      } catch (_) {/* sessionStorage may be blocked — proceed anyway */}
      onUnlocked(r.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="mt-10 max-w-sm mx-auto border border-slate-200 dark:border-slate-800 rounded-xl p-6 bg-slate-50 dark:bg-slate-900/50"
      data-testid="recipient-unlock-form"
    >
      <div className="w-12 h-12 rounded-full bg-cyan-500/10 grid place-items-center text-cyan-600 dark:text-cyan-400 mx-auto mb-4">
        <Lock className="w-5 h-5" />
      </div>
      <h2 className="font-display text-xl font-bold tracking-tight text-center mb-1">
        Verify it's you
      </h2>
      <p className="text-sm text-slate-600 dark:text-slate-400 text-center mb-5">
        Enter the <strong className="text-slate-900 dark:text-white">last 4 digits</strong> of the phone number this parcel was registered to.
      </p>
      <div className="relative mb-3">
        <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input
          data-testid="recipient-phone-last4"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={4}
          placeholder="••••"
          value={last4}
          onChange={(e) => setLast4(e.target.value)}
          className="pl-10 text-center tracking-[0.5em] text-lg font-mono-tech"
          autoFocus
        />
      </div>
      {error && (
        <div data-testid="recipient-unlock-error" className="text-xs text-red-600 dark:text-red-400 text-center mb-3">
          {error}
        </div>
      )}
      <Button
        type="submit"
        data-testid="recipient-unlock-submit"
        disabled={loading}
        className="w-full bg-cyan-500 hover:bg-cyan-400 dark:bg-cyan-400 dark:hover:bg-cyan-300 text-slate-950 font-medium h-11"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
        Unlock details
      </Button>
      <p className="text-[11px] text-slate-500 text-center mt-4">
        Don't have the registered phone?{" "}
        <Link to={`/track/${code}`} className="underline hover:text-slate-900 hover:dark:text-white">
          Use public tracking
        </Link>
      </p>
    </form>
  );
}

function DetailRow({ label, children, testId }) {
  return (
    <div className="grid grid-cols-[110px,1fr] gap-3 py-2.5 border-b border-slate-200/60 dark:border-slate-800/60 last:border-0">
      <div className="text-[10px] font-mono-tech uppercase tracking-[0.18em] text-slate-500 self-center">
        {label}
      </div>
      <div data-testid={testId} className="text-sm text-slate-900 dark:text-white">
        {children}
      </div>
    </div>
  );
}

function PrivateView({ code, data, onLock }) {
  const proof = data.delivery_proof;
  return (
    <div className="mt-10 space-y-6" data-testid="recipient-private-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <StatusPill status={data.status} label={data.status_label} />
          <span className="text-[11px] font-mono-tech text-slate-500">{data.package_id}</span>
        </div>
        <button
          onClick={onLock}
          data-testid="recipient-lock-btn"
          className="text-[11px] font-mono-tech uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900 hover:dark:text-white"
        >
          Lock view ×
        </button>
      </div>

      <section className="border border-slate-200 dark:border-slate-800 rounded-xl p-6 bg-slate-50 dark:bg-slate-900/40">
        <div className="flex items-center gap-2 text-cyan-600 dark:text-cyan-400 mb-1 text-[10px] font-mono-tech uppercase tracking-[0.2em]">
          <ShieldCheck className="w-3.5 h-3.5" /> / Private Details
        </div>
        <h2 className="font-display text-2xl font-bold tracking-tight mb-4">Recipient info</h2>
        <DetailRow label="Name" testId="recipient-name">{data.receiver?.name || "—"}</DetailRow>
        <DetailRow label="Phone" testId="recipient-phone">{data.receiver?.phone || "—"}</DetailRow>
        <DetailRow label="Address" testId="recipient-address">
          <span className="flex items-start gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
            {data.receiver?.address || "—"}
          </span>
        </DetailRow>
        <DetailRow label="Store">
          <span className="inline-flex items-center gap-1.5">
            <StoreIcon className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
            {data.store?.name || "—"}
          </span>
        </DetailRow>
        {data.rider?.name && (
          <DetailRow label="Rider">
            <span className="inline-flex items-center gap-1.5">
              <Bike className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
              {data.rider.name}
            </span>
          </DetailRow>
        )}
        <DetailRow label="Created">{new Date(data.created_at).toLocaleString()}</DetailRow>
        {data.delivered_at && (
          <DetailRow label="Delivered">{new Date(data.delivered_at).toLocaleString()}</DetailRow>
        )}
      </section>

      {proof && (proof.proof_photo || proof.signature_image) && (
        <section
          data-testid="recipient-proof-section"
          className="border border-green-500/40 rounded-xl p-6 bg-green-500/5"
        >
          <div className="flex items-center gap-2 text-green-700 dark:text-green-300 mb-1 text-[10px] font-mono-tech uppercase tracking-[0.2em]">
            <ShieldCheck className="w-3.5 h-3.5" /> / Delivery Proof
          </div>
          <h2 className="font-display text-xl font-bold tracking-tight mb-4">Captured at handoff</h2>

          {proof.proof_photo && (
            <div className="mb-4">
              <div className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1.5">
                <Camera className="w-3 h-3" /> Package photo
              </div>
              <img
                src={proof.proof_photo}
                alt="Delivery proof"
                data-testid="recipient-proof-photo"
                className="w-full rounded-lg border border-slate-200 dark:border-slate-800"
              />
            </div>
          )}

          {proof.signature_image && (
            <div className="mb-2">
              <div className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1.5">
                <PenLine className="w-3 h-3" /> Signature
              </div>
              <div className="bg-white rounded-lg p-3 border border-slate-200 dark:border-slate-800">
                <img
                  src={proof.signature_image}
                  alt="Signature"
                  data-testid="recipient-proof-signature"
                  className="w-full max-h-40 object-contain"
                />
              </div>
            </div>
          )}

          <div className="text-xs text-slate-600 dark:text-slate-400 mt-3 font-mono-tech">
            {proof.receiver_signed_name && (
              <div>Signed by: <span className="text-slate-900 dark:text-white">{proof.receiver_signed_name}</span></div>
            )}
            {proof.captured_at && (
              <div>
                {new Date(proof.captured_at).toLocaleString()}
                {proof.captured_by_rider_name ? ` · by ${proof.captured_by_rider_name}` : ""}
              </div>
            )}
          </div>
        </section>
      )}

      <div className="text-center pt-2">
        <Link
          to={`/track/${code}`}
          data-testid="recipient-back-public"
          className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 hover:dark:text-white"
        >
          ← Back to public tracking
        </Link>
      </div>
    </div>
  );
}

export default function RecipientView() {
  const { code } = useParams();
  const [data, setData] = useState(() => {
    if (!code) return null;
    try {
      const raw = sessionStorage.getItem(SESSION_KEY(code));
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  });

  // While unlocked, refresh in-place whenever the server pushes a status flip
  // so the recipient sees DELIVERED + proof appear without reloading.
  useEffect(() => {
    if (!data?.package_id) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        // Recipient endpoint requires re-verification; instead we use the public
        // tracking endpoint to refresh status + delivered_at, and merge it onto
        // the cached private record without leaking anything.
        const r = await api.get(`/public/track/${encodeURIComponent(code)}`);
        if (cancelled) return;
        const prev = data.status;
        const next = r.data?.status;
        if (next && next !== prev) {
          const merged = {
            ...data,
            status: next,
            status_label: r.data.status_label,
            updated_at: r.data.updated_at,
            delivered_at: r.data.delivered_at || data.delivered_at,
          };
          setData(merged);
          try { sessionStorage.setItem(SESSION_KEY(code), JSON.stringify(merged)); } catch (_) {/* noop */}
          toast.success(`Update: ${r.data.status_label}`);
        }
      } catch (e) { console.debug("recipient poll failed:", e); }
    };
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.package_id]);

  // Subscribe to live push so refreshes happen instantly (not via polling).
  useTrackingWebSocket(code, async () => {
    if (!data?.package_id) return;
    try {
      const r = await api.get(`/public/track/${encodeURIComponent(code)}`);
      const prev = data.status;
      const next = r.data?.status;
      if (next && next !== prev) {
        const merged = {
          ...data,
          status: next,
          status_label: r.data.status_label,
          updated_at: r.data.updated_at,
          delivered_at: r.data.delivered_at || data.delivered_at,
        };
        setData(merged);
        try { sessionStorage.setItem(SESSION_KEY(code), JSON.stringify(merged)); } catch (_) {/* noop */}
        toast.success(`Update: ${r.data.status_label}`);
      }
    } catch (e) { console.debug("recipient WS refresh failed:", e); }
  });

  const lock = () => {
    try { sessionStorage.removeItem(SESSION_KEY(code)); } catch (_) {/* noop */}
    setData(null);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white">
      <header className="sticky top-0 z-30 glass border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="recipient-brand-link">
            <Logo className="h-9" />
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to={`/track/${code}`}
              className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 hover:dark:text-white inline-flex items-center gap-1.5"
              data-testid="recipient-public-link"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Public view
            </Link>
            <ThemeToggle size="sm" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400 mb-2">
          / Recipient
        </div>
        <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tighter mb-2">
          Your parcel, up close.
        </h1>
        <p className="text-slate-600 dark:text-slate-400 text-sm">
          The full delivery record — name, address, signature, and photo proof.
        </p>

        {data ? (
          <PrivateView code={code} data={data} onLock={lock} />
        ) : (
          <UnlockGate code={code} onUnlocked={setData} />
        )}
      </main>
    </div>
  );
}
